import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CreateJournalSupportRequest, journalSupportsApi } from "@/api";
import { JournalSupportFormValues } from "@/common/components/journal-support/JournalSupportSection.types";
import {
  getTodayAt1530,
  journalSupportFormSchema,
} from "@/common/components/journal-support/JournalSupportSection.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface Props {
  onSuccess: () => void;
}

export function useJournalSupportCreation({ onSuccess }: Props) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (request: CreateJournalSupportRequest) =>
      journalSupportsApi.createJournalSupport({ createJournalSupportRequest: request }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.JournalSupports] });
      showSuccess(t("calendar.journalSupport.successRegister"));
      onSuccess();
    },
  });

  const formik = useFormikWithDirtyForm<JournalSupportFormValues>({
    initialValues: {
      startTime: new Date(),
      endTime: getTodayAt1530(),
    },
    validationSchema: journalSupportFormSchema(t),
    onSubmit: async (values: JournalSupportFormValues) => {
      await mutation.mutateAsync({
        endTimeUtc: values.endTime,
      });
    },
  });

  return { formik };
}
